/**
 * Client ของ Outsourcing (`/v1/outsource-orders`, `/v1/outsource-return-discrepancies`,
 * D-103) — ตาม route ที่ router จริงของเซิร์ฟเวอร์ serve
 * (`modules/outsourcing/router.py`), ไม่ใช่แค่ตาม `docs/API_CONTRACT.md`
 *
 * เจ็ดเส้นที่หน้าจอนี้ใช้: `GET`/`POST /v1/outsource-orders`, `POST .../lines`,
 * `POST .../ready`, `POST .../dispatch`, `POST .../receive` และ
 * `POST /v1/outsource-return-discrepancies/{id}/approve` — ไม่มี `GET` แยกของ
 * process master หรือ discrepancy ในไฟล์นี้ เพราะหน้าจอนี้ไม่ต้องใช้ (process ถูก
 * พิมพ์เป็นโค้ดตรง ๆ เหมือนสินค้า/ที่เก็บในหน้าจออื่น และ discrepancy ไม่มี `GET`
 * ในทั้ง contract — ดูหมายเหตุยาวใน `types.ts`)
 *
 * **ฝาแฝดของ `lib/packing/client.ts` ไม่ใช่ผู้ใช้ของมัน** — เหตุผลเดียวกับที่ไฟล์
 * นั้นไม่ import จาก `lib/procurement/client.ts`: แต่ละโดเมนมีชุดความล้มเหลว/โครง
 * เป็นของตัวเองโดยตั้งใจ
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่มีใบ"
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว — header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  AddOutsourceOrderLineBody,
  CreateOutsourceOrderBody,
  DispatchOutsourceOrderBody,
  OutsourceDispatchResult,
  OutsourceMovement,
  OutsourceOrder,
  OutsourceOrderLine,
  OutsourceOrderListPage,
  OutsourceReturnDiscrepancy,
  OutsourceReturnLine,
  OutsourceReturnResult,
  OutsourceTag,
  ReceiveOutsourceReturnBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const OUTSOURCE_ORDERS_PATH = '/v1/outsource-orders';
export const OUTSOURCE_RETURN_DISCREPANCIES_PATH = '/v1/outsource-return-discrepancies';

/** `Idempotency-Key` ที่ `POST .../receive` ต้องการ (400 ถ้าไม่ส่ง) */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

export function outsourceOrderPath(outsourceOrderId: string): string {
  return `${OUTSOURCE_ORDERS_PATH}/${encodeURIComponent(outsourceOrderId)}`;
}

export function outsourceOrderLinesPath(outsourceOrderId: string): string {
  return `${outsourceOrderPath(outsourceOrderId)}/lines`;
}

export function outsourceOrderReadyPath(outsourceOrderId: string): string {
  return `${outsourceOrderPath(outsourceOrderId)}/ready`;
}

export function outsourceOrderDispatchPath(outsourceOrderId: string): string {
  return `${outsourceOrderPath(outsourceOrderId)}/dispatch`;
}

export function outsourceOrderReceivePath(outsourceOrderId: string): string {
  return `${outsourceOrderPath(outsourceOrderId)}/receive`;
}

export function outsourceReturnDiscrepancyApprovePath(discrepancyId: string): string {
  return `${OUTSOURCE_RETURN_DISCREPANCIES_PATH}/${encodeURIComponent(discrepancyId)}/approve`;
}

/**
 * พารามิเตอร์ของ `GET /v1/outsource-orders` ที่ router รับจริง
 *
 * ทั้งสองแคบลงได้เท่านั้น ไม่เคยกว้างขึ้น: เซิร์ฟเวอร์กรองตาม `plant_scope` ของ
 * ผู้เรียกก่อนเสมอ และ `plant_code` นอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface OutsourceOrderListQuery {
  readonly plant_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildOutsourceOrderListQuery(query: OutsourceOrderListQuery = {}): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจโดยไม่เพิ่ม top-level
   * error code ใหม่ */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/** ความล้มเหลวทุกแบบที่ router นี้ตอบจริง แยกเป็นสถานะของตัวเอง */
export type OutsourceFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type OutsourceOrderListResult =
  | { readonly status: 'ready'; readonly page: OutsourceOrderListPage }
  | OutsourceFailure;

export type OutsourceOrderDetailResult =
  | { readonly status: 'ready'; readonly order: OutsourceOrder }
  | OutsourceFailure;

export type OutsourceDispatchDetailResult =
  | { readonly status: 'ready'; readonly result: OutsourceDispatchResult }
  | OutsourceFailure;

export type OutsourceReturnDetailResult =
  | { readonly status: 'ready'; readonly result: OutsourceReturnResult }
  | OutsourceFailure;

export type OutsourceDiscrepancyDetailResult =
  | { readonly status: 'ready'; readonly discrepancy: OutsourceReturnDiscrepancy }
  | OutsourceFailure;

export interface OutsourceRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function requiredInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/** `undefined` แปลว่า "ผิดรูป" — คีย์ที่หายไปทั้งคีย์หรือมาเป็นชนิดผิด ถือว่าผิดรูป
 * เท่ากัน ส่วน `null` (ตามที่ `Optional[...] = None` ของ Pydantic serialize ออกมา)
 * เป็นค่าที่ถูกต้อง */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

export function parseOutsourceOrderLine(raw: unknown): OutsourceOrderLine | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lineNo = requiredInteger(record.line_no);
  const productionOrderId = requiredString(record.production_order_id);
  const orderNo = requiredString(record.order_no);
  const operationSeq = requiredInteger(record.operation_seq);
  const quantity = requiredString(record.quantity);
  const uomCode = requiredString(record.uom_code);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    lineNo === null ||
    productionOrderId === null ||
    orderNo === null ||
    operationSeq === null ||
    quantity === null ||
    uomCode === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    line_no: lineNo,
    production_order_id: productionOrderId,
    order_no: orderNo,
    operation_seq: operationSeq,
    quantity,
    uom_code: uomCode,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseOutsourceOrder(raw: unknown): OutsourceOrder | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const outsourceOrderId = requiredString(record.outsource_order_id);
  const outsourceOrderNo = requiredString(record.outsource_order_no);
  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const supplierId = requiredString(record.supplier_id);
  const supplierCode = requiredString(record.supplier_code);
  const outsourceProcessId = requiredString(record.outsource_process_id);
  const processCode = requiredString(record.process_code);
  const expectedReturnDate = requiredString(record.expected_return_date);
  const status = requiredString(record.status);
  const createdByEmployeeId = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    outsourceOrderId === null ||
    outsourceOrderNo === null ||
    plantId === null ||
    plantCode === null ||
    supplierId === null ||
    supplierCode === null ||
    outsourceProcessId === null ||
    processCode === null ||
    expectedReturnDate === null ||
    status === null ||
    createdByEmployeeId === null ||
    createdAt === null ||
    updatedAt === null ||
    !Array.isArray(record.lines)
  ) {
    return null;
  }

  const readyByEmployeeId = nullableString(record.ready_by_employee_id);
  const readyAt = nullableString(record.ready_at);
  const dispatchedByEmployeeId = nullableString(record.dispatched_by_employee_id);
  const dispatchedAt = nullableString(record.dispatched_at);
  const note = nullableString(record.note);

  if (
    readyByEmployeeId === undefined ||
    readyAt === undefined ||
    dispatchedByEmployeeId === undefined ||
    dispatchedAt === undefined ||
    note === undefined
  ) {
    return null;
  }

  const lines: OutsourceOrderLine[] = [];
  for (const entry of record.lines) {
    const line = parseOutsourceOrderLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }

  return {
    outsource_order_id: outsourceOrderId,
    outsource_order_no: outsourceOrderNo,
    plant_id: plantId,
    plant_code: plantCode,
    supplier_id: supplierId,
    supplier_code: supplierCode,
    outsource_process_id: outsourceProcessId,
    process_code: processCode,
    expected_return_date: expectedReturnDate,
    status,
    created_by_employee_id: createdByEmployeeId,
    ready_by_employee_id: readyByEmployeeId,
    ready_at: readyAt,
    dispatched_by_employee_id: dispatchedByEmployeeId,
    dispatched_at: dispatchedAt,
    note,
    lines,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseOutsourceOrderListPage(raw: unknown): OutsourceOrderListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: OutsourceOrder[] = [];
  for (const entry of record.items) {
    const parsed = parseOutsourceOrder(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseOutsourceMovement(raw: unknown): OutsourceMovement | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const ledgerId = requiredString(record.ledger_id);
  const storageLocationId = requiredString(record.storage_location_id);
  const storageLocationCode = requiredString(record.storage_location_code);
  const lotId = requiredString(record.lot_id);
  const qty = requiredString(record.qty);
  const movementType = requiredString(record.movement_type);
  const referenceDocType = requiredString(record.reference_doc_type);
  const referenceDocId = requiredString(record.reference_doc_id);

  if (
    ledgerId === null ||
    storageLocationId === null ||
    storageLocationCode === null ||
    lotId === null ||
    qty === null ||
    movementType === null ||
    referenceDocType === null ||
    referenceDocId === null
  ) {
    return null;
  }

  return {
    ledger_id: ledgerId,
    storage_location_id: storageLocationId,
    storage_location_code: storageLocationCode,
    lot_id: lotId,
    qty,
    movement_type: movementType,
    reference_doc_type: referenceDocType,
    reference_doc_id: referenceDocId,
  };
}

function parseOutsourceMovements(raw: unknown): OutsourceMovement[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  const movements: OutsourceMovement[] = [];
  for (const entry of raw) {
    const movement = parseOutsourceMovement(entry);
    if (movement === null) {
      return null;
    }
    movements.push(movement);
  }
  return movements;
}

function parseOutsourceTag(raw: unknown): OutsourceTag | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const tagCode = requiredString(record.tag_code);
  const lineNo = requiredInteger(record.line_no);
  const lotId = requiredString(record.lot_id);
  const sourceLocationCode = requiredString(record.source_location_code);
  const quantity = requiredString(record.quantity);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    tagCode === null ||
    lineNo === null ||
    lotId === null ||
    sourceLocationCode === null ||
    quantity === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return {
    tag_code: tagCode,
    line_no: lineNo,
    lot_id: lotId,
    source_location_code: sourceLocationCode,
    quantity,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseOutsourceDispatchResult(raw: unknown): OutsourceDispatchResult | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const order = parseOutsourceOrder(record.order);
  const destinationLocationId = requiredString(record.destination_location_id);
  const destinationLocationCode = requiredString(record.destination_location_code);
  const movements = parseOutsourceMovements(record.movements);

  if (
    order === null ||
    destinationLocationId === null ||
    destinationLocationCode === null ||
    movements === null ||
    !Array.isArray(record.tags)
  ) {
    return null;
  }

  const tags: OutsourceTag[] = [];
  for (const entry of record.tags) {
    const tag = parseOutsourceTag(entry);
    if (tag === null) {
      return null;
    }
    tags.push(tag);
  }

  return {
    order,
    destination_location_id: destinationLocationId,
    destination_location_code: destinationLocationCode,
    tags,
    movements,
  };
}

function parseOutsourceReturnDiscrepancy(raw: unknown): OutsourceReturnDiscrepancy | null | undefined {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const record = raw as Record<string, unknown>;

  const discrepancyId = requiredString(record.discrepancy_id);
  const discrepancyType = requiredString(record.discrepancy_type);
  const status = requiredString(record.status);
  const expectedQty = requiredString(record.expected_qty);
  const actualQty = requiredString(record.actual_qty);
  const raisedByEmployeeId = requiredString(record.raised_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    discrepancyId === null ||
    discrepancyType === null ||
    status === null ||
    expectedQty === null ||
    actualQty === null ||
    raisedByEmployeeId === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return undefined;
  }

  const approvedByEmployeeId = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  const reason = nullableString(record.reason);

  if (approvedByEmployeeId === undefined || approvedAt === undefined || reason === undefined) {
    return undefined;
  }

  return {
    discrepancy_id: discrepancyId,
    discrepancy_type: discrepancyType,
    status,
    expected_qty: expectedQty,
    actual_qty: actualQty,
    raised_by_employee_id: raisedByEmployeeId,
    approved_by_employee_id: approvedByEmployeeId,
    approved_at: approvedAt,
    reason,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

function parseOutsourceReturnLine(raw: unknown): OutsourceReturnLine | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lineNo = requiredInteger(record.line_no);
  const tagCode = requiredString(record.tag_code);
  const dispatchedLotId = requiredString(record.dispatched_lot_id);
  const dispatchedQuantity = requiredString(record.dispatched_quantity);
  const returnedQuantity = requiredString(record.returned_quantity);
  const varianceQuantity = requiredString(record.variance_quantity);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);

  if (
    lineNo === null ||
    tagCode === null ||
    dispatchedLotId === null ||
    dispatchedQuantity === null ||
    returnedQuantity === null ||
    varianceQuantity === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const returnedLotId = nullableString(record.returned_lot_id);
  const varianceReason = nullableString(record.variance_reason);
  const qualityStatus = nullableString(record.quality_status);
  const discrepancy = parseOutsourceReturnDiscrepancy(record.discrepancy);

  if (
    returnedLotId === undefined ||
    varianceReason === undefined ||
    qualityStatus === undefined ||
    discrepancy === undefined
  ) {
    return null;
  }

  return {
    line_no: lineNo,
    tag_code: tagCode,
    dispatched_lot_id: dispatchedLotId,
    returned_lot_id: returnedLotId,
    dispatched_quantity: dispatchedQuantity,
    returned_quantity: returnedQuantity,
    variance_quantity: varianceQuantity,
    variance_reason: varianceReason,
    quality_status: qualityStatus,
    discrepancy,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseOutsourceReturnResult(raw: unknown): OutsourceReturnResult | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const outsourceReturnId = requiredString(record.outsource_return_id);
  const outsourceReturnNo = requiredString(record.outsource_return_no);
  const order = parseOutsourceOrder(record.order);
  const receivingLocationId = requiredString(record.receiving_location_id);
  const receivingLocationCode = requiredString(record.receiving_location_code);
  const subcontractorLocationId = requiredString(record.subcontractor_location_id);
  const subcontractorLocationCode = requiredString(record.subcontractor_location_code);
  const receivedByEmployeeId = requiredString(record.received_by_employee_id);
  const receivedAt = requiredString(record.received_at);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  const movements = parseOutsourceMovements(record.movements);

  if (
    outsourceReturnId === null ||
    outsourceReturnNo === null ||
    order === null ||
    receivingLocationId === null ||
    receivingLocationCode === null ||
    subcontractorLocationId === null ||
    subcontractorLocationCode === null ||
    receivedByEmployeeId === null ||
    receivedAt === null ||
    createdAt === null ||
    updatedAt === null ||
    movements === null ||
    !Array.isArray(record.lines)
  ) {
    return null;
  }

  const note = nullableString(record.note);
  if (note === undefined) {
    return null;
  }

  const lines: OutsourceReturnLine[] = [];
  for (const entry of record.lines) {
    const line = parseOutsourceReturnLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }

  return {
    outsource_return_id: outsourceReturnId,
    outsource_return_no: outsourceReturnNo,
    order,
    receiving_location_id: receivingLocationId,
    receiving_location_code: receivingLocationCode,
    subcontractor_location_id: subcontractorLocationId,
    subcontractor_location_code: subcontractorLocationCode,
    received_by_employee_id: receivedByEmployeeId,
    received_at: receivedAt,
    note,
    lines,
    movements,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

export function parseOutsourceDiscrepancy(raw: unknown): OutsourceReturnDiscrepancy | null {
  const parsed = parseOutsourceReturnDiscrepancy(raw);
  return parsed === undefined ? null : parsed;
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/** เหลือเฉพาะคีย์ที่ contract รับ — เขียนทีละตัวแทนการ spread เพื่อไม่ให้ฟิลด์ที่
 * เซิร์ฟเวอร์เป็นเจ้าของ (`status`, `outsource_order_no`, ...) หลุดไปกับ state ฟอร์ม */
export function serializeCreateOutsourceOrderBody(
  body: CreateOutsourceOrderBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    supplier_code: body.supplier_code,
    process_code: body.process_code,
    expected_return_date: body.expected_return_date,
    ...(body.note === undefined ? {} : { note: body.note }),
  };
}

export function serializeAddOutsourceOrderLineBody(
  body: AddOutsourceOrderLineBody,
): Record<string, unknown> {
  return {
    order_no: body.order_no,
    operation_seq: body.operation_seq,
    quantity: body.quantity,
    uom_code: body.uom_code,
  };
}

export function serializeDispatchOutsourceOrderBody(
  body: DispatchOutsourceOrderBody,
): Record<string, unknown> {
  return {
    destination_location_code: body.destination_location_code,
    packages: body.packages.map((pkg) => ({
      line_no: pkg.line_no,
      lot_id: pkg.lot_id,
      source_location_code: pkg.source_location_code,
      quantity: pkg.quantity,
    })),
  };
}

export function serializeReceiveOutsourceReturnBody(
  body: ReceiveOutsourceReturnBody,
): Record<string, unknown> {
  return {
    receiving_location_code: body.receiving_location_code,
    packages: body.packages.map((pkg) => ({
      tag_code: pkg.tag_code,
      returned_quantity: pkg.returned_quantity,
      ...(pkg.variance_quantity === undefined
        ? {}
        : { variance_quantity: pkg.variance_quantity }),
      ...(pkg.variance_reason === undefined ? {} : { variance_reason: pkg.variance_reason }),
    })),
    ...(body.note === undefined ? {} : { note: body.note }),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | OutsourceFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: OutsourceRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/outsource-orders` — กรองตามขอบเขตโรงงานของผู้เรียกเสมอ */
export async function fetchOutsourceOrders(
  query: OutsourceOrderListQuery = {},
  options: OutsourceRequestOptions = {},
): Promise<OutsourceOrderListResult> {
  const result = await requestPayload(
    `${OUTSOURCE_ORDERS_PATH}${buildOutsourceOrderListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const page = parseOutsourceOrderListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

function toOrderResult(result: PayloadResult): OutsourceOrderDetailResult {
  if (result.status !== 'payload') {
    return result;
  }
  const order = parseOutsourceOrder(result.payload);
  return order === null ? { status: 'invalid-response' } : { status: 'ready', order };
}

/** `POST /v1/outsource-orders` — เปิดใบที่ `DRAFT` ไม่มีบรรทัด ตอบ `201` */
export async function createOutsourceOrder(
  body: CreateOutsourceOrderBody,
  options: OutsourceRequestOptions = {},
): Promise<OutsourceOrderDetailResult> {
  return toOrderResult(
    await requestPayload(
      OUTSOURCE_ORDERS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateOutsourceOrderBody(body)),
      },
      options,
    ),
  );
}

/** `POST /v1/outsource-orders/{id}/lines` — หนึ่งเรียกหนึ่งบรรทัด ตอบใบเต็ม `201` */
export async function addOutsourceOrderLine(
  outsourceOrderId: string,
  body: AddOutsourceOrderLineBody,
  options: OutsourceRequestOptions = {},
): Promise<OutsourceOrderDetailResult> {
  return toOrderResult(
    await requestPayload(
      outsourceOrderLinesPath(outsourceOrderId),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeAddOutsourceOrderLineBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/outsource-orders/{id}/ready` — `DRAFT → READY_TO_DISPATCH` (APPROVE)
 *
 * body ว่างเปล่าเสมอ — ไม่มี `expected_status` ในสัญญานี้เลย (module docstring
 * ของ router: การ์ดสถานะต้นทางตอบ `409` พร้อม `details.current_status` อยู่แล้ว)
 */
export async function markOutsourceOrderReady(
  outsourceOrderId: string,
  options: OutsourceRequestOptions = {},
): Promise<OutsourceOrderDetailResult> {
  return toOrderResult(
    await requestPayload(
      outsourceOrderReadyPath(outsourceOrderId),
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/outsource-orders/{id}/dispatch` — ย้ายสต๊อกจริง ตอบ `200` เสมอ
 * (ทั้งครั้งแรกและตอน replay — ดู module docstring ของ router)
 */
export async function dispatchOutsourceOrder(
  outsourceOrderId: string,
  body: DispatchOutsourceOrderBody,
  options: OutsourceRequestOptions = {},
): Promise<OutsourceDispatchDetailResult> {
  const result = await requestPayload(
    outsourceOrderDispatchPath(outsourceOrderId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeDispatchOutsourceOrderBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const dispatch = parseOutsourceDispatchResult(result.payload);
  return dispatch === null ? { status: 'invalid-response' } : { status: 'ready', result: dispatch };
}

/**
 * `POST /v1/outsource-orders/{id}/receive` — ย้ายสต๊อกกลับ ตอบ `201` เสมอ
 * (ไม่มี replay ตามสถานะเอกสาร — ใช้ `Idempotency-Key` แทน เพราะหนึ่งใบมารับกลับ
 * ได้หลายเที่ยว)
 */
export async function receiveOutsourceReturn(
  outsourceOrderId: string,
  body: ReceiveOutsourceReturnBody,
  idempotencyKey: string,
  options: OutsourceRequestOptions = {},
): Promise<OutsourceReturnDetailResult> {
  const result = await requestPayload(
    outsourceOrderReceivePath(outsourceOrderId),
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [IDEMPOTENCY_KEY_HEADER]: idempotencyKey,
      },
      body: JSON.stringify(serializeReceiveOutsourceReturnBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const parsed = parseOutsourceReturnResult(result.payload);
  return parsed === null ? { status: 'invalid-response' } : { status: 'ready', result: parsed };
}

/**
 * `POST /v1/outsource-return-discrepancies/{id}/approve` — **การอนุมัติเป็นการ
 * กระทำของตัวเอง แยกขาดจากการรับกลับ** (invariant "THE APPROVAL IS SEPARATE")
 * body ว่างเปล่าเสมอ
 */
export async function approveOutsourceReturnDiscrepancy(
  discrepancyId: string,
  options: OutsourceRequestOptions = {},
): Promise<OutsourceDiscrepancyDetailResult> {
  const result = await requestPayload(
    outsourceReturnDiscrepancyApprovePath(discrepancyId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }
  const discrepancy = parseOutsourceDiscrepancy(result.payload);
  return discrepancy === null
    ? { status: 'invalid-response' }
    : { status: 'ready', discrepancy };
}

/** อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี */
export function ruleFromFailure(failure: OutsourceFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/* -------------------------------------------------- Idempotency-Key ของการรับกลับ */
/**
 * **หนึ่งการรับกลับหนึ่งคีย์** — ฝาแฝดของ `lib/goodsReceipts/idempotency.ts`
 * restated ที่นี่แทนการ import ข้ามโดเมน ด้วยเหตุผลเดียวกับที่ไฟล์นั้นเขียนไว้เอง
 * ("ถ้าวันหนึ่งทั้งสองที่ต้องใช้ตัวสร้างตัวเดียวกัน มันควรถูกยกขึ้นไปเป็น helper
 * กลาง ไม่ใช่ให้ฝั่งหนึ่ง import ข้ามเข้าไปในคิวของอีกฝั่ง") — retry ของฟอร์มเดิม
 * (เนื้อ body เดียวกัน) ต้องได้คีย์เดิม ไม่งั้นการกดซ้ำหลัง network error จะกลาย
 * เป็นการรับกลับสองเที่ยว
 */
export interface OutsourceReturnSubmission {
  readonly fingerprint: string;
  readonly key: string;
}

export function newIdempotencyKey(): string {
  const webCrypto = (globalThis as { crypto?: Crypto }).crypto;

  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join(
      '-',
    );
  }

  throw new Error('เครื่องนี้ไม่มีแหล่งสุ่มที่ปลอดภัยพอจะสร้าง Idempotency-Key');
}

export function outsourceReturnSubmissionFor(
  previous: OutsourceReturnSubmission | null,
  fingerprint: string,
  generate: () => string = newIdempotencyKey,
): OutsourceReturnSubmission {
  if (previous !== null && previous.fingerprint === fingerprint) {
    return previous;
  }
  return { fingerprint, key: generate() };
}
